<template>
  <v-page no-padding white>
    <div class="h-100 bg-white">
      <thread-header
        is-new
        :users="toSubscribers"
        :parent-message="parentMessage"
      />

      <template v-if="isGroupChat && !isPro">
        <thread-prompt
          icon="quickreply"
          heading="Upgrade To Pro"
          description="Group Chat is available on the Pro Plan - upgrade to access"
          to="https://my.movement.so/plans?ref_source=in-app-group-messaging&ref_content=new-message"
          button-text="Upgrade Now"
          padding-top="15vh"
        />
      </template>
      <template v-else>
        <template v-if="isLoading">
          <thread-placeholder class="mb-1" />
        </template>
        <template v-else>
          <thread-prompt
            :icon="parentMessage ? 'reply' : 'forum'"
            :heading="promptHeading"
            :description="promptDescription"
            :padding-top="!!parentMessage ? '7.5vh' : '15vh'"
          />
        </template>

        <div class="container">
          <div :class="[{ 'ml-3': !!parentMessage }]">
            <thread-footer
              :is-thread="!!parentMessage"
              :is-comment-thread="!!parentMessage"
            >
              <message-compose
                :is-loading="isLoading || isCreating"
                :placeholder="
                  parentMessage ? 'Add your reply' : 'Write something...'
                "
                @submit="handleSubmit"
              />
            </thread-footer>
          </div>
        </div>
      </template>
    </div>
  </v-page>
</template>

<script>
import { mapActions, mapGetters } from "vuex"

import { pluralize, isPresent } from "@/lib/utils"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { parseMessageId } from "@/lib/messages-utils"

import ThreadPrompt from "@/components/message-thread/Prompt"
import ThreadFooter from "@/components/message-thread/Footer"
import MessageCompose from "@/components/message-thread/MessageCompose"
import ThreadHeader from "@/components/message-thread/Header"
import ThreadPlaceholder from "@/components/message-thread/ThreadPlaceholder"

export default {
  components: {
    ThreadPrompt,
    ThreadFooter,
    ThreadHeader,
    MessageCompose,
    ThreadPlaceholder
  },

  data() {
    return {
      isLoading: false,
      isCreating: false,
      toSubscribers: [],
      parentMessage: null
    }
  },

  computed: {
    ...mapGetters(["subscription"]),

    isNewChat() {
      return isPresent(this.$route.query.users)
    },

    isPrivateChat() {
      return this.isNewChat && this.toSubscribersCount === 1
    },

    isGroupChat() {
      return this.isNewChat && this.toSubscribersCount > 1
    },

    isReplyThread() {
      return isPresent(this.$route.query.message)
    },

    toSubscribersCount() {
      return this.toSubscribers.length
    },

    isPro() {
      return this.subscription?.is_pro
    },

    promptHeading() {
      if (this.toSubscribersCount > 0) {
        return "Send your message"
      } else if (this.parentMessage) {
        return `Reply to ${this.parentMessage?.user?.name}`
      }

      return null
    },

    promptDescription() {
      if (this.isPrivateChat) {
        return `Send a message to start your conversation with ${this.toSubscribers[0].name}`
      } else if (this.isGroupChat) {
        return `Send a message to start your conversation with ${
          this.toSubscribersCount
        } ${pluralize("Member", this.toSubscribersCount)}`
      } else {
        return `Write a message to start a thread`
      }
    }
  },

  activated() {
    this.initializeForm()
  },

  deactivated() {
    this.toSubscribers = []
    this.parentMessage = null
  },

  mounted() {
    this.initializeForm()
  },

  methods: {
    isPresent,

    ...mapActions("messages", [
      "fetchSubscribers",
      "fetchMessage",
      "createMessageThread"
    ]),

    initializeForm() {
      this.isLoading = true

      if (this.isNewChat) {
        // Although the "users" query param is used, it refers to the `subscriber_id` not the `user_id`
        // Confusing, I know... but its more user friendly for those snooping in the URL
        this.fetchSubscribers(this.$route.query.users.split(","))
          .then(({ subscribers }) => {
            this.toSubscribers = subscribers
          })
          .finally(() => {
            this.isLoading = false
          })
      } else if (this.isReplyThread) {
        const [messageThreadId, messageTs] = parseMessageId(
          this.$route.query.message
        )

        this.fetchMessage({
          messageThreadId,
          messageTs
        })
          .then(message => {
            this.parentMessage = message
          })
          .finally(() => {
            this.isLoading = false
          })
      } else {
        this.$router.replace("/me/messages")
      }
    },

    handleSubmit(messages) {
      this.isCreating = true

      this.createMessageThread({
        subscriberIds: this.toSubscribers.map(s => s.id).filter(Boolean),
        parentMessage: this.parentMessage,
        messages
      })
        .then(messageThread => {
          this.$router.replace(
            `/me/messages/${messageThread.id}?focus=true&reply=true`
          )

          setTimeout(() => {
            EventBus.$emit(EVENTS.PROMPT_PUSH_PERMISSION)
          }, 500)
        })
        .catch(err => {
          this.$notify(err.response.data.message)
        })
        .finally(() => {
          this.isCreating = false
        })
    }
  }
}
</script>
